import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, Trash2, X } from "lucide-react";

/**
 * iter216 — MissingEmployeesResolver
 * Classifies every "Missing from Payroll" row for a (month, centre) so
 * the reconciliation red banner clears once every mismatch is
 * legitimately accounted for.
 */
export const CATEGORIES = [
  { code: "VALID_FOR_PAYROLL",     label: "Valid for Payroll",     hint: "Should be paid — run Sync from Attendance" },
  { code: "TRANSFERRED",           label: "Transferred",            hint: "Paid at another centre or already handed off" },
  { code: "EXITED",                label: "Exited",                 hint: "Left the company / F&F closed" },
  { code: "NOT_ACTIVE_FOR_MONTH",  label: "Not Active For Month",   hint: "On sabbatical / no-pay leave / not yet joined" },
  { code: "MASTER_RECORD_MISSING", label: "Master Record Missing",  hint: "No Employee Master row — needs HR fix" },
  { code: "CENTRE_MAPPING_ERROR",  label: "Centre Mapping Error",   hint: "Employee mapped to wrong centre" },
];

const dash = (v) => (v && String(v).trim() ? String(v) : "—");

function toneForSystemReason(r) {
  if (!r || r === "UNCLASSIFIED") return "bg-slate-100 text-slate-700";
  if (r.startsWith("TRANSFERRED")) return "bg-blue-100 text-blue-800";
  if (r === "MASTER RECORD MISSING") return "bg-red-100 text-red-800";
  if (r === "NOT ACTIVE FOR MONTH") return "bg-amber-100 text-amber-800";
  if (r === "CENTRE MAPPING ERROR") return "bg-orange-100 text-orange-800";
  return "bg-slate-100 text-slate-700";
}

export function MissingEmployeesResolver({ token, month, center, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});   // { normName: {category, reason} }
  const [savingAll, setSavingAll] = useState(false);

  const load = async () => {
    if (!month || !center) return;
    setLoading(true);
    try {
      const { data } = await api.post("/salary_reconciliation", {
        token, month, targetCenter: center,
      });
      const details = data?.missing_from_payroll_details || [];
      setRows(details);
      const seed = {};
      for (const d of details) {
        seed[d.employee_name] = {
          category: d.exclusion_category || "",
          reason: d.exclusion_reason || "",
        };
      }
      setEdits(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, month, center]);

  const summary = useMemo(() => {
    const total = rows.length;
    const classified = rows.filter(r => (edits[r.employee_name]?.category || r.exclusion_category)).length;
    const valid = rows.filter(r => (edits[r.employee_name]?.category || r.exclusion_category) === "VALID_FOR_PAYROLL").length;
    const excluded = rows.filter(r => {
      const cat = edits[r.employee_name]?.category || r.exclusion_category;
      return cat && cat !== "VALID_FOR_PAYROLL";
    }).length;
    return { total, classified, unresolved: total - classified, valid, excluded };
  }, [rows, edits]);

  const setCell = (name, key, val) => setEdits(e => ({ ...e, [name]: { ...(e[name] || {}), [key]: val }}));

  const saveRow = async (row) => {
    const e = edits[row.employee_name] || {};
    if (!e.category) { toast.error("Pick a category first"); return; }
    try {
      await api.post("/payroll_exclusion/save", {
        token, month, center,
        employee_name: row.employee_name,
        category: e.category,
        reason: e.reason || "",
      });
      toast.success(`Saved: ${row.employee_name}`);
      await load();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const clearRow = async (row) => {
    try {
      await api.post("/payroll_exclusion/clear", {
        token, month, center, employee_name: row.employee_name,
      });
      toast.success(`Cleared: ${row.employee_name}`);
      await load();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    }
  };

  const saveAll = async () => {
    setSavingAll(true);
    let ok = 0, fail = 0;
    for (const row of rows) {
      const e = edits[row.employee_name] || {};
      if (!e.category) continue;
      // Skip if identical to saved
      if (e.category === row.exclusion_category && (e.reason || "") === (row.exclusion_reason || "")) continue;
      try {
        await api.post("/payroll_exclusion/save", {
          token, month, center,
          employee_name: row.employee_name,
          category: e.category,
          reason: e.reason || "",
        });
        ok += 1;
      } catch { fail += 1; }
    }
    setSavingAll(false);
    if (ok || fail) toast.success(`Saved ${ok}${fail ? ` · ${fail} failed` : ""}`);
    await load();
    onSaved && onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
                     data-testid="missing-resolver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Resolve Missing from Payroll — <span className="font-mono">{center}</span> · {month}</span>
            <Badge variant="outline">Total: {summary.total}</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Valid: {summary.valid}</Badge>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Excluded: {summary.excluded}</Badge>
            {summary.unresolved > 0 ? (
              <Badge variant="destructive">Unresolved: {summary.unresolved}</Badge>
            ) : (
              <Badge className="bg-emerald-600 text-white">All resolved</Badge>
            )}
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Classify each employee so the reconciliation banner clears. Rows marked{" "}
            <b>Valid For Payroll</b> should be added back via <b>Sync from Attendance</b> →{" "}
            <b>Regenerate</b>. All other categories stay excluded with an audited reason.
          </div>
        </DialogHeader>

        <div className="overflow-auto flex-1 border rounded" data-testid="missing-resolver-table">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow className="text-[11px]">
                <TableHead>Name</TableHead>
                <TableHead>Emp ID</TableHead>
                <TableHead className="text-right">Att Days</TableHead>
                <TableHead>Current Centre</TableHead>
                <TableHead>Effective Centre</TableHead>
                <TableHead>Join Date</TableHead>
                <TableHead>Exit Date</TableHead>
                <TableHead>Transfer</TableHead>
                <TableHead>Master</TableHead>
                <TableHead>System Reason</TableHead>
                <TableHead className="min-w-[200px]">Classify As *</TableHead>
                <TableHead className="min-w-[200px]">Reason / Note</TableHead>
                <TableHead className="w-[130px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-[12px]">
              {loading && (
                <TableRow><TableCell colSpan={13} className="text-center py-8">
                  <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />Loading…
                </TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                  No missing employees for this centre / month. Nothing to resolve.
                </TableCell></TableRow>
              )}
              {rows.map(r => {
                const e = edits[r.employee_name] || {};
                const dirty = (e.category || "") !== (r.exclusion_category || "")
                              || (e.reason || "") !== (r.exclusion_reason || "");
                return (
                  <TableRow key={r.employee_name}
                    data-testid={`missing-row-${r.employee_name.replace(/\s+/g,'-')}`}>
                    <TableCell className="font-medium">{dash(r.employee_name)}</TableCell>
                    <TableCell className="font-mono text-[11px]">{dash(r.employee_id)}</TableCell>
                    <TableCell className="text-right">{r.attendance_days || 0}</TableCell>
                    <TableCell>{dash(r.current_center)}</TableCell>
                    <TableCell>{dash(r.effective_center)}</TableCell>
                    <TableCell className="text-[11px]">{dash(r.join_date)}</TableCell>
                    <TableCell className="text-[11px]">{dash(r.exit_date)}</TableCell>
                    <TableCell className="text-[11px]">{dash(r.transfer_status)}</TableCell>
                    <TableCell className="text-[11px]">{dash(r.master_status)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${toneForSystemReason(r.system_reason)}`}>
                        {dash(r.system_reason)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Select value={e.category || ""}
                        onValueChange={(v) => setCell(r.employee_name, "category", v)}>
                        <SelectTrigger className="h-8 text-[11px]"
                          data-testid={`missing-cat-${r.employee_name.replace(/\s+/g,'-')}`}>
                          <SelectValue placeholder="— Select —" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(c => (
                            <SelectItem key={c.code} value={c.code} className="text-[11px]">
                              <div className="flex flex-col">
                                <span>{c.label}</span>
                                <span className="text-[10px] text-muted-foreground">{c.hint}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input value={e.reason || ""}
                        placeholder="e.g. Transferred to PB-HSR on 12-Aug"
                        className="h-8 text-[11px]"
                        onChange={(ev) => setCell(r.employee_name, "reason", ev.target.value)}
                        data-testid={`missing-reason-${r.employee_name.replace(/\s+/g,'-')}`} />
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => saveRow(r)}
                        disabled={!e.category || !dirty}
                        className="h-7 px-2 text-[11px]"
                        data-testid={`missing-save-${r.employee_name.replace(/\s+/g,'-')}`}>
                        <Save className="w-3 h-3 mr-1" />Save
                      </Button>
                      {r.exclusion_category && (
                        <Button size="sm" variant="ghost" onClick={() => clearRow(r)}
                          className="h-7 w-7 p-0"
                          data-testid={`missing-clear-${r.employee_name.replace(/\s+/g,'-')}`}
                          title="Clear classification">
                          <Trash2 className="w-3.5 h-3.5 text-red-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="pt-2">
          <div className="mr-auto text-[11px] text-muted-foreground">
            Tip: <b>Valid For Payroll</b> stays unresolved-looking until you also run
            <b> Sync from Attendance</b> to add them into the snapshot.
          </div>
          <Button variant="outline" onClick={onClose} data-testid="missing-resolver-close">
            <X className="w-4 h-4 mr-1" />Close
          </Button>
          <Button onClick={saveAll} disabled={savingAll || loading || rows.length === 0}
            data-testid="missing-resolver-save-all">
            {savingAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
